import { createContext, type ReactNode, use } from 'react'
import {
  TagGroup as AriaTagGroup,
  TagList as AriaTagList,
  Tag as AriaTag,
} from 'react-aria-components'
import cx from 'classnames'
import styles from './Chip.module.css'
import { RawButton } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { useKeyboard } from 'react-aria'
import { SkeletonOverlay, useIsSkeleton } from '../Skeleton'

export type ChipVariant = 'default' | 'danger' | 'neutral' | 'success'

interface LabelChipProps {
  /**
   * Text to display in the chip
   */
  text: string

  /**
   * Variant of the chip
   */
  variant?: ChipVariant

  /**
   * Whether the chip is a skeleton
   */
  isSkeleton?: boolean

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Basic chip component, used for labels.
 */
export function LabelChip({
  text,
  variant = 'default',
  isSkeleton,
  testId,
}: LabelChipProps) {
  isSkeleton = useIsSkeleton(isSkeleton)

  return (
    <div
      data-test={testId}
      className={cx(
        styles.labelChip,
        isSkeleton && styles.skeleton,
        variant === 'default' && styles.default,
        variant === 'danger' && styles.danger,
        variant === 'neutral' && styles.neutral,
        variant === 'success' && styles.success,
      )}
    >
      {isSkeleton ? (
        <span>
          {text || 'Loading'}
          <SkeletonOverlay />
        </span>
      ) : (
        text
      )}
    </div>
  )
}

interface InputChipListProps {
  /**
   * Children to render inside the chip list
   */
  children?: ReactNode

  /**
   * Label for the chip list
   */
  'aria-label'?: string

  /**
   * ID of the element to use as a label for the chip list
   */
  'aria-labelledby'?: string

  /**
   * ID of the element to use as a hint for the chip list.
   */
  'aria-describedby'?: string

  /**
   * Function to remove a chip
   */
  onRemove?: (keys: Set<string>) => unknown

  /**
   * Function to open the menu via keyboard using the Enter key
   */
  onRequestMenuOpen?: () => unknown

  /**
   * Whether the chip list is disabled
   */
  isDisabled?: boolean

  /**
   * Keys that should be disabled.
   */
  disabledKeys?: Set<string>
}

/**
 * Context for propagating disabled state to child components
 */
const IsDisabledInternalContext = createContext(false)

/**
 * Input Chip List
 */
export function InputChipList({
  children,
  onRemove,
  isDisabled,
  onRequestMenuOpen,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  disabledKeys,
}: InputChipListProps) {
  const { keyboardProps } = useKeyboard({
    isDisabled,
    onKeyDown: (evt) => {
      if (evt.key === 'Enter') {
        evt.preventDefault()
        onRequestMenuOpen?.()
      }
    },
  })

  return (
    <IsDisabledInternalContext value={isDisabled ?? false}>
      <AriaTagGroup
        disabledKeys={disabledKeys}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        className={styles.inputChipListGroup}
        // @ts-ignore: we know IDs are always strings
        onRemove={onRemove}
      >
        <AriaTagList {...keyboardProps} className={styles.inputChipList}>
          {children}
        </AriaTagList>
      </AriaTagGroup>
    </IsDisabledInternalContext>
  )
}

interface InputChipProps {
  /**
   * Text to display in the chip
   */
  text: string

  /**
   * Variant of the chip
   */
  variant?: ChipVariant

  /**
   * ID of the chip, used for selection tracking
   */
  id?: string

  /**
   * Whether the chip is disabled
   */
  isDisabled?: boolean

  /**
   * Whether the chip is a skeleton
   */
  isSkeleton?: boolean

  /**
   * Test ID attribute for the chip.
   */
  testId?: string

  /**
   * Test ID attribute for the remove button.
   */
  removeButtonTestId?: string
}

/**
 * Chip used for inputs.
 */
export function InputChip({
  isDisabled,
  text,
  id,
  variant = 'default',
  isSkeleton,
  testId,
  removeButtonTestId,
}: InputChipProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  isDisabled ??= use(IsDisabledInternalContext)

  return (
    <AriaTag
      id={id}
      data-test={testId}
      textValue={isSkeleton ? 'Loading' : text}
      isDisabled={isDisabled || isSkeleton}
      className={({ isFocused, isDisabled }) =>
        cx(
          styles.inputChip,
          isSkeleton && styles.skeleton,
          isFocused && styles.focused,
          isDisabled && styles.disabled,
          variant === 'default' && styles.default,
          variant === 'danger' && styles.danger,
          variant === 'neutral' && styles.neutral,
        )
      }
    >
      {({ allowsRemoving, isDisabled }) =>
        isSkeleton ? (
          <span>
            {text || 'Loading'}
            <SkeletonOverlay />
          </span>
        ) : (
          <>
            {text}
            {allowsRemoving && (
              <RawButton
                isDisabled={isDisabled}
                data-test={removeButtonTestId}
                className={({ isDisabled }) =>
                  cx(styles.removeButton, isDisabled && styles.disabled)
                }
                slot="remove"
              >
                <div className={styles.removeButtonIcon}>
                  <Icon mini name="close" />
                </div>
              </RawButton>
            )}
          </>
        )
      }
    </AriaTag>
  )
}
